import { useAbility } from "@casl/react";
import { useTranslation } from "react-i18next";

import PillTabsLayout from "../components/molecules/PillTabsLayout";
import {
  Integrations,
  RulesTable,
  StripePaymentSettings,
  StripeReadyForConnect,
  WebsiteSettings,
} from "../components/pages";
import { ClassesTab } from "../components/Settings/Activity/ClassesTab";
import { AppointableTable } from "../components/Settings/Appointable/AppointableTable";
import { AppSettings } from "../components/Settings/AppSettings/AppSettings";
import { ClubInfo } from "../components/Settings/ClubInfo/ClubInfo";
import { FacilitiesTab } from "../components/Settings/Facility/FacilitiesTab";
import GiftVouchersTab from "../components/Settings/GiftVouchers/GiftVouchersTab";
import { MembershipsTab } from "../components/Settings/Membership/MembershipsTab";
import { OnboardingSettings } from "../components/Settings/Onboarding/OnboardingSettings";
import { PaymentMethodsTab } from "../components/Settings/PaymentMethods/PaymentMethodsTab";
import { ProductsTab } from "../components/Settings/Product/ProductsTab";
import { CreditPackTab } from "../components/Settings/Sessions/CreditPackTab";
import { SettingsRoute } from "../components/Settings/SettingsRoute";
import { SMSTab } from "../components/Settings/SMS/SMSTab";
import { StaffTable } from "../components/Settings/Staff/StaffTable";
import { usePortalRoutes } from "../hooks/usePortalRoutes";
import { AbilityContext, Subject, Verb } from "../permissions";
import { useClubSettings } from "../providers";
import { RouteFeature } from "../routes/feature";
import { RouteLayout } from "../routes/layout";

export function Settings() {
  const { createClubLink } = usePortalRoutes();
  const ability = useAbility(AbilityContext);
  const { t } = useTranslation();

  const { club, featureFlags } = useClubSettings();

  const layoutConfig = [
    {
      text: t("page.settings.tabTitle.club"),
      path: createClubLink(
        RouteLayout.Staff,
        RouteFeature.Settings,
        SettingsRoute.Club,
      ),
      component: ClubInfo,
    },
    {
      text: t("page.settings.tabTitle.sms"),
      path: createClubLink(
        RouteLayout.Staff,
        RouteFeature.Settings,
        SettingsRoute.SMS,
      ),
      component: SMSTab,
    },
    {
      text: t("page.settings.tabTitle.memberships"),
      path: createClubLink(
        RouteLayout.Staff,
        RouteFeature.Settings,
        SettingsRoute.MembershipType,
      ),
      component: MembershipsTab,
    },
    {
      text: t("page.settings.tabTitle.sessions"),
      path: createClubLink(
        RouteLayout.Staff,
        RouteFeature.Settings,
        SettingsRoute.Sessions,
      ),
      component: CreditPackTab,
    },
    {
      text: t("page.settings.tabTitle.products"),
      path: createClubLink(
        RouteLayout.Staff,
        RouteFeature.Settings,
        SettingsRoute.Product,
      ),
      component: ProductsTab,
    },
    {
      text: t("page.settings.tabTitle.payments"),
      path: createClubLink(
        RouteLayout.Staff,
        RouteFeature.Settings,
        SettingsRoute.StripePayments,
      ),
      component:
        club.stripeAccountType === "READY_TO_CONNECT"
          ? StripeReadyForConnect
          : StripePaymentSettings,
      invisible:
        (ability.cannot(Verb.View, Subject.StripeConnect) &&
          ability.cannot(Verb.Create, Subject.StripeConnect)) ||
        !ability.can(Verb.View, Subject.Payments),
    },
    {
      text: t("page.settings.tabTitle.facilities"),
      path: createClubLink(
        RouteLayout.Staff,
        RouteFeature.Settings,
        SettingsRoute.Facility,
      ),
      component: FacilitiesTab,
    },
    {
      text: t("page.settings.tabTitle.classes"),
      path: createClubLink(
        RouteLayout.Staff,
        RouteFeature.Settings,
        SettingsRoute.Activity,
      ),
      component: ClassesTab,
    },
    {
      text: t("page.settings.tabTitle.appointments"),
      path: createClubLink(
        RouteLayout.Staff,
        RouteFeature.Settings,
        SettingsRoute.Appointable,
      ),
      component: AppointableTable,
      invisible: !ability.can(Verb.View, Subject.Appointments),
    },
    {
      text: t("page.settings.tabTitle.staff"),
      path: createClubLink(
        RouteLayout.Staff,
        RouteFeature.Settings,
        SettingsRoute.Staff,
      ),
      component: StaffTable,
      invisible: !ability.can(Verb.View, Subject.StaffAccount),
    },
    {
      text: t("page.settings.tabTitle.rules"),
      path: createClubLink(
        RouteLayout.Staff,
        RouteFeature.Settings,
        SettingsRoute.Rule,
      ),
      component: RulesTable,
    },
    {
      text: t("page.settings.tabTitle.website"),
      path: createClubLink(
        RouteLayout.Staff,
        RouteFeature.Settings,
        SettingsRoute.Hosted,
      ),
      component: WebsiteSettings,
    },
    {
      text: t("page.settings.tabTitle.appSettings"),
      path: createClubLink(
        RouteLayout.Staff,
        RouteFeature.Settings,
        SettingsRoute.AppSettings,
      ),
      component: AppSettings,
    },
    {
      text: t("page.settings.tabTitle.onboarding"),
      path: createClubLink(
        RouteLayout.Staff,
        RouteFeature.Settings,
        SettingsRoute.Forms,
      ),
      component: OnboardingSettings,
    },
    {
      text: t("page.settings.tabTitle.integrations"),
      path: createClubLink(
        RouteLayout.Staff,
        RouteFeature.Settings,
        SettingsRoute.Integrations,
      ),
      component: Integrations,
      invisible: !ability.can(Verb.View, Subject.Integrations),
    },
    {
      text: t("page.settings.tabTitle.paymentMethods"),
      path: createClubLink(
        RouteLayout.Staff,
        RouteFeature.Settings,
        SettingsRoute.PaymentMethods,
      ),
      component: PaymentMethodsTab,
      invisible: !ability.can(Verb.View, Subject.PaymentMethods),
    },
    {
      text: t("page.settings.tabTitle.giftVouchers"),
      path: createClubLink(
        RouteLayout.Staff,
        RouteFeature.Settings,
        SettingsRoute.GiftVouchers,
      ),
      component: GiftVouchersTab,
      invisible:
        !ability.can(Verb.View, Subject.GiftVouchers) ||
        !featureFlags.FE_VOUCHER_CHECKOUT,
    },
  ];

  return (
    <div className="flex size-full max-h-full p-4 lg:p-8">
      <PillTabsLayout tabs={layoutConfig} enableAnimations />
    </div>
  );
}
